import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { loadStoredSession } from "@itaas/core/auth/auth.storage";
import {
  buildSuiteLoginUrl,
} from "@itaas/core/auth/suite-redirect";
import {
  markSsoRelayAttempt,
  shouldBreakSsoRelayLoop,
} from "@itaas/core/auth/sso-relay-guard";
import {
  buildModuleHandoffUrl,
  clearReturnTo,
  loadReturnToPath,
  saveReturnToModuleId,
  saveReturnToPath,
} from "@itaas/core/modules/suite-navigation";
import { SUITE_MODULE_REGISTRY } from "@itaas/core/modules/registry";
import { getModuleBaseUrl, getModuleDefaultPath } from "@/config";
import { routes } from "@/router/all_routes";

/** Relais SSO : si Suite a déjà une session, handoff direct vers le module cible. */
export function ModuleRelayPage() {
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const handledRef = useRef(false);

  useEffect(() => {
    if (handledRef.current) return;
    handledRef.current = true;

    const targetModule = searchParams.get("targetModule");
    const targetPath =
      searchParams.get("targetPath") ?? loadReturnToPath() ?? undefined;
    const session = loadStoredSession();

    if (!targetModule) {
      window.location.replace(routes.modules);
      return;
    }

    if (!session?.tokens?.accessToken) {
      if (targetModule) saveReturnToModuleId(targetModule);
      if (targetPath?.startsWith("/")) saveReturnToPath(targetPath);
      window.location.replace(
        buildSuiteLoginUrl({
          suiteUrl: window.location.origin,
          moduleId: targetModule,
          returnTo: targetPath,
        }),
      );
      return;
    }

    if (shouldBreakSsoRelayLoop(targetModule)) {
      clearReturnTo();
      window.location.replace(
        `${buildSuiteLoginUrl({
          suiteUrl: window.location.origin,
        })}?stay=1`,
      );
      return;
    }

    markSsoRelayAttempt(targetModule);
    const entry = SUITE_MODULE_REGISTRY.find((module) => module.id === targetModule);
    if (!entry) {
      setError(`Module « ${targetModule} » introuvable.`);
      return;
    }

    try {
      const baseUrl = getModuleBaseUrl(targetModule);
      const path = targetPath ?? getModuleDefaultPath(targetModule);
      const url = buildModuleHandoffUrl(baseUrl, entry.handoffPath, session, path);
      window.location.replace(url);
    } catch (relayError) {
      const message =
        relayError instanceof Error
          ? relayError.message
          : "Ouverture du module impossible.";
      setError(message);
    }
  }, [searchParams]);

  if (error) {
    return (
      <div className="suite-center-message">
        <p className="text-danger mb-3">{error}</p>
        <a
          href={buildSuiteLoginUrl({
            suiteUrl: window.location.origin,
            moduleId: searchParams.get("targetModule") ?? undefined,
          })}
          className="btn btn-primary btn-sm"
        >
          Se connecter
        </a>
      </div>
    );
  }

  return (
    <div className="suite-center-message">
      <p className="text-muted mb-0">Ouverture du module…</p>
    </div>
  );
}
